const PAGE_WIDTHS = {
  A4: 595.28,
  LETTER: 612,
};

/**
 * Default page margins for PDF generation.
 * @constant
 * @type {Object}
 * @property {number} top - Top margin in points.
 * @property {number} bottom - Bottom margin in points.
 * @property {number} left - Left margin in points.
 * @property {number} right - Right margin in points.
 */
export const PAGE_MARGINS = {
  top: 30,
  bottom: 30,
  left: 30,
  right: 30,
};

export const PAGE_MARGIN_X = PAGE_MARGINS.left + PAGE_MARGINS.right;
export const PAGE_MARGIN_Y = PAGE_MARGINS.top + PAGE_MARGINS.bottom;

/**
 * The maximum width (in pixels) allowed for a PDF page.
 * @todo Fix for LETTER size pages
 * @type {number}
 */
export const MAX_PAGE_WIDTH = PAGE_WIDTHS.A4 - PAGE_MARGIN_X;

/**
 * Maximum allowed height for a PDF page in pixels.
 * @type {number}
 */
export const MAX_PAGE_HEIGHT = 800;

/**
 * Scaling factor used for PDF-related calculations to fit images and tables.
 *
 * @todo This is an approximated value based on images scaled to 50% in the editor
 * and should be tested for different page sizes.
 *
 * @constant
 * @type {number}
 */
export const PDF_SCALE_FACTOR = MAX_PAGE_WIDTH / 784;

/**
 * The width of the table border in PDF documents.
 * @constant
 * @type {number}
 */
export const WIDTH_OF_TABLE_BORDER = 0.0175;

// @todo Docs
export const TABLE_COLS_DEFAULT_QUANTITY = 3;

// @todo Docs
export const INITIAL_MULTIPLIER_VALUE = 1;

// @todo Docs
export const MINIMUM_CELL_WIDTH = 1;

/**
 * Value represents the fill color opacity of the merged vertically table cell containing image
 * so that image won't be covered by fill color
 * @constant
 * @type {number}
 */
export const MERGED_HEADER_CELL_FILL_OPACITY = 0.07;

/**
 * Default styles for PDF elements.
 *
 * @constant
 * @type {Object}
 * @property {Object} img - Styles for image elements.
 * @property {number} img.maxWidth - Maximum width for images.
 * @property {number} img.maxHeight - Maximum height for images.
 * @property {number[]} img.margin - Margin around images [left, top, right, bottom].
 * @property {Object} table - Styles for table elements.
 * @property {number} table.width - Width for tables.
 * @property {Object} p - Styles for paragraph elements.
 * @property {number[]} p.margin - Margin around paragraphs [left, top, right, bottom].
 */
export const DEFAULT_PDF_STYLES = {
  img: {
    maxWidth: MAX_PAGE_WIDTH,
    maxHeight: MAX_PAGE_HEIGHT,
    margin: [0, 12, 0, 12],
  },
  table: {
    width: MAX_PAGE_WIDTH,
  },
  p: {
    margin: [0, 0, 0, 0],
  },

}; // DEFAULT_PDF_STYLES()

/**
 * Size in points of the checkbox drawn for a checklist item.
 * @constant
 * @type {number}
 */
export const TASK_CHECKBOX_SIZE = 8;

/**
 * Width in points of the column holding the checkbox — the box plus breathing room at body size.
 * @constant
 * @type {number}
 */
export const TASK_CHECKBOX_COLUMN_WIDTH = 11;

/**
 * Gap in points between the checkbox column and the checklist item's content.
 * @constant
 * @type {number}
 */
export const TASK_CHECKBOX_GAP = 3;

/**
 * Top offset to align the box with text baseline.
 * @constant
 * @type {number}
 */
export const TASK_CHECKBOX_TOP_OFFSET = 2;

/**
 * Diameter in points of the bullet drawn for a centered or right-aligned list item — pdfmake's own disc is
 * fontSize / 3 across, and the exports use a 9pt body.
 * @constant
 */
export const LIST_BULLET_SIZE = /** @type {const} */ (3);

/**
 * Top offset to put the bullet where pdfmake draws its own disc.
 * @constant
 */
export const LIST_BULLET_TOP_OFFSET = /** @type {const} */ (3.5);

/**
 * Gap in points between a drawn bullet and the item's text, matching pdfmake's own.
 * @constant
 */
export const LIST_BULLET_GAP = /** @type {const} */ (4);

/**
 * Gap in points between a drawn number and the item's text — the space pdfmake puts after its own "1.".
 * @constant
 */
export const LIST_NUMBER_GAP = /** @type {const} */ (2);
